import { orgSelectors } from 'ducks/orgs'
import { StyledNavLinkProps } from 'layout/headers/elements/StyledNavLink'
import { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { useCashflowSetupOutstanding, useGetCashFlowNavVisible } from 'util/cashFlow'
import { useFeatureFlag } from 'util/featureFlags'
import useMarketplaceOnNavbarEnabled from './useMarketplaceOnNavbarEnabled'

type NavigationLinkType = {
  badgeSeverity?: StyledNavLinkProps['badgeSeverity']
  label: string
  key: string
  uiKey: string
  value?: string
}

type BaseNavigationLink = Omit<NavigationLinkType, 'uiKey'>

export const HOME_NAVIGATION_LINK: BaseNavigationLink = {
  key: 'home',
  label: 'Home [App Navigation]',
}

export const CONTROL_NAVIGATION_LINK: BaseNavigationLink = {
  key: 'control',
  label: 'Control',
}

export const PROJECTS_NAVIGATION_LINK: BaseNavigationLink = {
  key: 'projects',
  label: 'Projects',
  value: 'crm',
}

const BASE_NAVIGATION_LINKS: BaseNavigationLink[] = [CONTROL_NAVIGATION_LINK]

export const EXPO_NAVIGATION_LINK: Omit<NavigationLinkType, 'uiKey'> = {
  key: 'expo',
  label: 'Expo',
}
export const HEADER_NAV_UI_KEY = 'header.nav'

const useNavigationLinks = (): NavigationLinkType[] => {
  const enableHardwareOrdering = useFeatureFlag('hardware_ordering', 'on')
  const cashFlowOnboardingStatus = useSelector(orgSelectors.getCashFlowOnboardingStatus)
  const cashFlowEnabled = useGetCashFlowNavVisible()
  const hasOutstandingSetupActions = useCashflowSetupOutstanding()
  const isNav3Enabled = useFeatureFlag('nav_3', 'on')
  const isNearmapOrg = useSelector(orgSelectors.getIsNearmapOrg)
  const { isShopEnabled, isExpoEnabled, isExpoHookEnabled } = useMarketplaceOnNavbarEnabled()
  const proFeaturesEnabled = useSelector(orgSelectors.getProFeaturesEnabled)

  const isCashflowActive = useSelector(orgSelectors.getOrg)?.cashflow_is_active

  const cashFlowBadgeSeverity = useMemo(() => {
    if (isCashflowActive) {
      if (hasOutstandingSetupActions) {
        return isNearmapOrg ? 'info-secondary' : 'info'
      }
      return undefined
    }
    if (cashFlowOnboardingStatus === 'created' || cashFlowOnboardingStatus === null) {
      if (isNearmapOrg) return 'info-secondary'
      else return 'info'
    } else if (cashFlowOnboardingStatus === 'conditionally approved' || cashFlowOnboardingStatus === 'pending') {
      return 'error'
    }
    return undefined
  }, [isCashflowActive, hasOutstandingSetupActions, cashFlowOnboardingStatus, isNearmapOrg])

  return useMemo<NavigationLinkType[]>(() => {
    const navLinks: BaseNavigationLink[] = [...BASE_NAVIGATION_LINKS]
    if (!isNav3Enabled) navLinks.unshift(HOME_NAVIGATION_LINK)
    if (proFeaturesEnabled) navLinks.unshift(PROJECTS_NAVIGATION_LINK)

    if (cashFlowEnabled && proFeaturesEnabled) {
      navLinks.push({
        badgeSeverity: cashFlowBadgeSeverity,
        key: 'cashflow',
        label: 'CashFlow',
      })
    }

    if (isShopEnabled) {
      navLinks.push({ key: 'shop', label: 'Shop' })
    }
    if (isExpoEnabled && isExpoHookEnabled) {
      navLinks.push({ key: 'expo', label: 'Expo' })
    }

    return navLinks.map((navLink) => ({
      ...navLink,
      uiKey: `${HEADER_NAV_UI_KEY}.${navLink.key}`,
      value: navLink.value || navLink.key,
    }))
  }, [
    cashFlowEnabled,
    cashFlowBadgeSeverity,
    enableHardwareOrdering,
    isNav3Enabled,
    isShopEnabled,
    isExpoEnabled,
    isExpoHookEnabled,
  ])
}

export default useNavigationLinks
